﻿import React, { useState, useEffect } from 'react';
import { GasChromatographResSolventsBenchSheet } from '../models/gas-chromatograph-res-solvents-bench-sheet';
import { BenchSheetTechnologyType, DocumentType, NoteType, Entities, AnalysisScopeType } from '../models/enums';
import { Document } from '../models/document';
import NoteViewer from './NoteViewer';
import GasChromatographyInstrument from './GasChromatographyInstrument';
import BenchSheetColumnInformation from './BenchSheetColumnInformation';
import GcRsReportedSolvents from './GcRsReportedSolvents';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import ColumnLogbookLookup from '../components/ColumnLogbookLookup';
import BenchSheetData from './BenchSheetData';
import SingleDocumentLoader from './SingleDocumentLoader';
import MinimumSampleSizeComponent from './MinimumSampleSizeComponent';
import SamplePreparationsComponent from './SamplePreparations/SamplePreparationsComponent';
import { CrudFunctions } from '../utilities/crud-functions';
import { DocumentationConstants } from '../store/documentation-constants';
import TicketLogEntryForm from './TicketLogEntryForm';
import { UserDataStore } from '../store/userDataStore';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import BenchSheetToolbar from './BenchSheetToolbar';
import { ItemBasicInformation } from '../models/item-basic-information';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 0;

export default function GasChromatographyResidualSolventsBenchSheetForm() {

    const [benchSheet, setBenchSheet] =
        useState<GasChromatographResSolventsBenchSheet>();

    const [gcColumnSearchDialogVisible, setGcColumnSearchDialogVisible] =
        useState<boolean>();

    const [gcRsDataFileName, setGcRsDataFileName] =
        useState<string>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        if (!dataLoaded)
            getAllData();
    }, []);


    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "QCBS-A", "QCBS-L", "QCBS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "QCBS-A", "QCBS-L"];
    const canInsert = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "QCBS-A"];
    const canDelete = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    return (
        <div className="pw-form-container">
            <div className="pw-content">
                <BenchSheetToolbar title="GC-RS Bench Sheet"
                    subtitle="Gas Chromatography - Residual Solvents"
                    auditRowPointer={benchSheetId} entity={Entities.GcRsBenchSheets}
                    technologyId={BenchSheetTechnologyType.GasChromatographyResidualSolvents}
                    documentationId={DocumentationConstants.GCRSBenchSheets}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                <GasChromatographyInstrument readOnly={!canEdit}
                    technology={BenchSheetTechnologyType.GasChromatographyResidualSolvents}
                    benchSheetId={benchSheetId} />

                <div className="pw-margin-bottom-05">
                    <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={true} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <NoteViewer height='12rem' title="Bench Sheet Notes" readOnly={!canEdit}
                        noteType={NoteType.BenchSheet} parentId={benchSheetId} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <BenchSheetColumnInformation benchSheetId={benchSheetId} readOnly={!canEdit}
                        technology={BenchSheetTechnologyType.GasChromatographyResidualSolvents} />
                </div>

                {(benchSheet && benchSheet.scopeTypes && benchSheet.scopeTypes.includes(AnalysisScopeType.Homogeneity)) &&
                    <MinimumSampleSizeComponent benchSheetId={benchSheetId} />
                }

                <div>
                    <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={false} />
                </div>

                <div className="pw-comp-row">
                    <SingleDocumentLoader label="GC-RS Data" readOnly={!canEdit}
                        documentName={gcRsDataFileName}
                        onDocumentLoaded={onGcRsDataFileLoaded} />
                </div>

                <GcRsReportedSolvents benchSheetId={benchSheetId} />

                <div className="pw-comp-row-scroll-fixed-grid">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={true}
                        technology={BenchSheetTechnologyType.GasChromatographyResidualSolvents}
                        documentType={DocumentType.General}
                        imageHandlerTitle="C of A Images" />
                </div>
                {/*<div>*/}
                {/*    <StandardsViewer batchId={benchSheet && benchSheet.batchId ?*/}
                {/*        benchSheet.batchId : undefined}*/}
                {/*        technologyId={BenchSheetTechnologyType.GasChromatographyResidualSolvents} />*/}
                {/*</div>*/}

                <DmaFooter />

                {gcColumnSearchDialogVisible &&
                    <DialogPopup visible={gcColumnSearchDialogVisible}
                        onHiding={hideGcColumnSearchDialog}
                        title="GC Column Search"
                        width={'65em'}
                        height={'45em'}>

                        <ColumnLogbookLookup
                            technology={BenchSheetTechnologyType.GasChromatographyResidualSolvents} />
                        {/* TODO: 
                            onCreateNewEntry={onCreateNewEntry}
                            onUseSelectedEntry={onUseSelectedEntry}
                            onCancel={hideGcColumnSearchDialog} />                        
                        */}


                    </DialogPopup>
                }

                {itemBasicInformationDialogVisible &&
                    <DialogPopup visible={itemBasicInformationDialogVisible}
                        onHiding={hideItemBasicInformationDialog}
                        title="Item Basic Information"
                        width={'25rem'}
                        height={'21rem'}
                        dragEnabled={false}
                        resizeEnabled={false}>
                        <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                            onOkClick={hideItemBasicInformationDialog}
                            key={`IBI${key}`} />
                    </DialogPopup>
                }

                {ticketLogEntryDialogVisible &&
                    <DialogPopup visible={ticketLogEntryDialogVisible}
                        onHiding={hideTicketLogEntryDialog}
                        title="Ticket Log Entry"
                        width={'55rem'}
                        height={'45rem'}
                        dragEnabled={true}
                        resizeEnabled={true}>
                        <TicketLogEntryForm formName="GC-RS Bench Sheet" contextData={benchSheet}
                            onCancel={hideTicketLogEntryDialog}
                            onComplete={hideTicketLogEntryDialog}
                            key={`TLE${key}`} />
                    </DialogPopup>
                }


                {globalDialogVisible &&
                    <GlobalDialog metadata={globalDialogMetadata} />
                }

            </div>
        </div>
    );

    async function onGcRsDataFileLoaded(gcrsDataFile: Document) {
        gcrsDataFile.documentType = DocumentType.Excel;

        //TODO fix this
        await this.uploadGcRsDataFile(gcrsDataFile)
            .then((data) => {

            })
            .then(() => {
                //getBenchSheet();
                //getDocuments();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Loading GC-RS Data File");
            });
    }

    // get functions

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }


    async function uploadGcRsDataFile(gcrsDataFile: Document) {

        //todo:
        return await CrudFunctions.postData(`benchsheets/gcrsfiledata/${benchSheetId}`, gcrsDataFile);


    }

    //navigation

    function goBack() {
        history.back();
    }

    // show/hide

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    //this is not right: review this
    //especially when and why it is called

    async function hideGcColumnSearchDialog(e: any) {
        //this.setState({ gcColumnSearchDialogVisible: false });

        //should this be called at the bottom?
        setGcColumnSearchDialogVisible(false);

        /*
        if (this.gcColumnSearchWindow && this.gcColumnSearchWindow.current && this.gcColumnSearchWindow.current.state) {
            let selectedHeader = this.gcColumnSearchWindow.current.state.selectedColumnHeader as GcColumnHeader;
            let selectedEntry = this.gcColumnSearchWindow.current.state.selectedColumnEntry as GcColumnEntry;

            if (selectedHeader) {
                let columnBrand = `${selectedHeader.description}, ${selectedHeader.length} m x ${selectedHeader.diameter} mm I.D., ${selectedHeader.filmThickness} µm film thickness`;

                this.setState((prevState: any) => ({
                    benchSheet: {
                        ...prevState.benchSheet,
                        columnBrand: columnBrand
                    }
                }));
            }

            if (selectedEntry) {
                let columnEntry = `${selectedEntry.entryNumber}`;

                this.setState((prevState: any) => ({
                    benchSheet: {
                        ...prevState.benchSheet,
                        columnIdLogbookEntryId: columnEntry
                    }
                }));
            }

            //needed?
            //this.htmlEditor.current.setState({ document: '' });            
        }
        */
    }

    async function getAllData() {
        getBenchSheet()
            .then(() => {
                setDataLoaded(true);
            });
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

